import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { propertyPath, extractPropertyId, propertyShareUrl, agentPath } from '@/utils/slug';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  Building2, Bed, Bath, MapPin, Heart, ArrowLeft, Share2,
  Maximize, Car, Trees, Shield, Phone, Mail, Calendar,
  CheckCircle, Home, Users, Ruler, IndianRupee, X, ChevronLeft, ChevronRight
} from 'lucide-react';
import AreaDisplay, { AreaBadge } from '@/components/AreaDisplay';
import ProgressiveImage from '@/components/ProgressiveImage';
import apiClient from '@/utils/api';

// Demo data removed — production fetches from API only

const PropertyDetails = () => {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const realId = extractPropertyId(propertyId);
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  
  // Form states
  const [contactForm, setContactForm] = useState({
    name: '',
    phone: '',
    email: '',
    message: ''
  });
  const [scheduleForm, setScheduleForm] = useState({
    name: '',
    phone: '',
    email: '',
    preferred_date: '',
    preferred_time: '',
    notes: ''
  });

  useEffect(() => {
    const fetchProperty = async () => {
      try {
        const response = await apiClient.get(`/properties/${realId}`);
        if (response.data) {
          setProperty(response.data);
          // Canonical URL normalization (slug) — keeps old-format links working
          const canonical = propertyPath(response.data);
          if (window.location.pathname !== canonical) {
            navigate(canonical, { replace: true });
          }
        }
      } catch (err) {
        console.error('Failed to fetch property:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProperty();
  }, [realId, navigate]);

  // Keyboard navigation for gallery
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!galleryOpen || !property) return;
      const imgs = property.images || [];
      if (!imgs.length) return;
      if (e.key === 'ArrowLeft') {
        setSelectedImage((prev) => (prev === 0 ? imgs.length - 1 : prev - 1));
      } else if (e.key === 'ArrowRight') {
        setSelectedImage((prev) => (prev === imgs.length - 1 ? 0 : prev + 1));
      } else if (e.key === 'Escape') {
        setGalleryOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [galleryOpen, property]);

  const formatPrice = (price) => {
    if (price >= 10000000) {
      return `₹${(price / 10000000).toFixed(2)} Cr`;
    } else if (price >= 100000) {
      return `₹${(price / 100000).toFixed(2)} Lac`;
    }
    return `₹${price.toLocaleString('en-IN')}`;
  };

  const handleShare = () => {
    const shareUrl = property ? propertyShareUrl(property) : window.location.href;
    if (navigator.share) {
      navigator.share({ title: property?.title || 'ZenStudios property', url: shareUrl }).catch(() => {});
    } else {
      navigator.clipboard.writeText(shareUrl);
      toast.success('Shareable link copied!');
    }
  };

  const isValidPhone = (phone) => {
    const cleaned = phone.replace(/[^0-9+\-\s()]/g, '').replace(/\s/g, '');
    return cleaned.length >= 10;
  };

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleContactSubmit = (e) => {
    e.preventDefault();
    if (!contactForm.name || !contactForm.phone) {
      toast.error('Please fill in required fields');
      return;
    }
    if (!isValidPhone(contactForm.phone)) {
      toast.error('Please enter a valid phone number');
      return;
    }
    if (contactForm.email && !isValidEmail(contactForm.email)) {
      toast.error('Please enter a valid email');
      return;
    }
    
    toast.success('Your inquiry has been sent! The seller will contact you shortly.');
    setContactModalOpen(false);
    setContactForm({ name: '', phone: '', email: '', message: '' });
    
    // Open WhatsApp for immediate connection
    const whatsappMsg = `Hi! I'm interested in the property: ${property.title} (${property.property_id}). My name is ${contactForm.name}.`;
    window.open(`https://wa.me/918790460259?text=${encodeURIComponent(whatsappMsg)}`, '_blank');
  };

  const handleScheduleSubmit = (e) => {
    e.preventDefault();
    if (!scheduleForm.name || !scheduleForm.phone || !scheduleForm.preferred_date) {
      toast.error('Please fill in required fields');
      return;
    }
    if (!isValidPhone(scheduleForm.phone)) {
      toast.error('Please enter a valid phone number');
      return;
    }
    if (scheduleForm.email && !isValidEmail(scheduleForm.email)) {
      toast.error('Please enter a valid email');
      return;
    }
    
    toast.success('Visit scheduled! You will receive a confirmation shortly.');
    setScheduleModalOpen(false);
    setScheduleForm({ name: '', phone: '', email: '', preferred_date: '', preferred_time: '', notes: '' });
    
    // Open WhatsApp for confirmation
    const whatsappMsg = `Hi! I'd like to schedule a visit for: ${property.title}. Preferred date: ${scheduleForm.preferred_date}, Time: ${scheduleForm.preferred_time || 'Flexible'}. My name is ${scheduleForm.name}.`;
    window.open(`https://wa.me/918790460259?text=${encodeURIComponent(whatsappMsg)}`, '_blank');
  };

  const navigateGallery = (direction) => {
    const imgs = property?.images || [];
    if (!imgs.length) return;
    if (direction === 'prev') {
      setSelectedImage((prev) => (prev === 0 ? imgs.length - 1 : prev - 1));
    } else {
      setSelectedImage((prev) => (prev === imgs.length - 1 ? 0 : prev + 1));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="animate-pulse text-gray-500">Loading property details...</div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen pt-20 flex flex-col items-center justify-center" data-testid="property-not-found">
        <h1 className="text-2xl font-bold mb-4">Property Not Found</h1>
        <p className="text-gray-600 mb-6">The property you&apos;re looking for doesn&apos;t exist or has been removed.</p>
        <Button onClick={() => navigate('/real-estate')} className="rounded-lg">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Properties
        </Button>
      </div>
    );
  }

  // Defensive normalization — backend may return nulls/absent keys for new listings
  const PLACEHOLDER_IMAGE = 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=1200&q=80';
  const rawImages = Array.isArray(property.images) ? property.images.filter(Boolean) : [];
  const images = rawImages.length ? rawImages : [PLACEHOLDER_IMAGE];
  const safeSelectedImage = Math.min(Math.max(selectedImage, 0), images.length - 1);
  const seller = property.seller || {
    name: property.owner_name || property.contact_name || 'Owner',
    type: property.owner_type || (property.agent_id ? 'Agent' : 'Owner'),
    phone: property.contact_phone || property.owner_phone || '',
    email: property.contact_email || property.owner_email || '',
  };
  const amenities = Array.isArray(property.amenities) ? property.amenities : [];
  const highlights = Array.isArray(property.highlights) ? property.highlights : [];

  return (
    <div className="min-h-screen bg-white pt-20" data-testid="property-details-page">
      <Helmet>
        <title>{`${property.title || 'Property'}${property.city ? ' in ' + property.city : ''} | ZenStudios`}</title>
        <meta name="description" content={(property.description || `${property.sub_type || ''} for ${property.listing_type || 'sale'} in ${property.city || 'India'}`).slice(0, 160)} />
        <link rel="canonical" href={`${window.location.origin}${propertyPath(property)}`} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={`${property.title || 'Property'} | ZenStudios`} />
        <meta property="og:description" content={(property.description || '').slice(0, 160)} />
        <meta property="og:url" content={`${window.location.origin}${propertyPath(property)}`} />
        <meta property="og:image" content={(property.images && property.images[0]) || 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=1200&q=80'} />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      {/* Back Navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
        <button 
          onClick={() => navigate('/real-estate')}
          className="flex items-center text-gray-600 hover:text-primary transition-colors"
          data-testid="back-to-listings"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to listings
        </button>
      </div>

      {/* Image Gallery */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 mb-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Main Image */}
          <div className="lg:col-span-2">
            <div 
              className="relative aspect-[16/10] rounded-xl overflow-hidden cursor-pointer group"
              onClick={() => setGalleryOpen(true)}
            >
              <ProgressiveImage 
                src={images[safeSelectedImage]} 
                alt={property.title}
                className="w-full h-full"
                data-testid="main-property-image"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                <span className="text-white opacity-0 group-hover:opacity-100 transition-opacity bg-black/50 px-4 py-2 rounded-full text-sm">
                  Click to view gallery
                </span>
              </div>
              <div className="absolute top-4 right-4 flex gap-2">
                <button 
                  onClick={(e) => { e.stopPropagation(); setIsWishlisted(!isWishlisted); }}
                  className={`p-2 rounded-full ${isWishlisted ? 'bg-red-500 text-white' : 'bg-white/90 text-gray-700'} hover:scale-110 transition-all`}
                  data-testid="wishlist-btn"
                >
                  <Heart className={`w-5 h-5 ${isWishlisted ? 'fill-current' : ''}`} />
                </button>
                <button 
                  onClick={(e) => { e.stopPropagation(); handleShare(); }}
                  className="p-2 rounded-full bg-white/90 text-gray-700 hover:scale-110 transition-all"
                  data-testid="share-btn"
                >
                  <Share2 className="w-5 h-5" />
                </button>
              </div>
              <div className="absolute bottom-4 left-4">
                <span className="bg-primary text-white px-3 py-1 rounded-full text-sm font-medium capitalize">
                  {property.property_subtype?.replace('_', ' ')}
                </span>
              </div>
              <div className="absolute bottom-4 right-4 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
                {safeSelectedImage + 1} / {images.length}
              </div>
            </div>
          </div>
          
          {/* Thumbnail Grid */}
          <div className="grid grid-cols-2 gap-2">
            {images.map((img, idx) => (
              <button 
                key={idx}
                onClick={() => setSelectedImage(idx)}
                className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                  safeSelectedImage === idx ? 'border-primary ring-2 ring-primary/30' : 'border-transparent hover:border-primary/50'
                }`}
                data-testid={`thumbnail-${idx}`}
              >
                <ProgressiveImage src={img} alt={`View ${idx + 1}`} className="w-full h-full" />
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Fullscreen Gallery Modal */}
      {galleryOpen && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          data-testid="gallery-modal"
        >
          <button 
            onClick={() => setGalleryOpen(false)}
            className="absolute top-4 right-4 text-white p-2 hover:bg-white/10 rounded-full transition-colors"
            data-testid="gallery-close-btn"
          >
            <X className="w-8 h-8" />
          </button>
          
          <button 
            onClick={() => navigateGallery('prev')}
            className="absolute left-4 text-white p-2 hover:bg-white/10 rounded-full transition-colors"
            data-testid="gallery-prev-btn"
          >
            <ChevronLeft className="w-10 h-10" />
          </button>
          
          <div className="flex items-center justify-center w-full h-full px-20 py-16">
            <img 
              src={images[safeSelectedImage]} 
              alt={`${property.title} - Image ${safeSelectedImage + 1}`}
              className="max-w-full max-h-full object-contain rounded-lg"
              loading="eager"
              data-testid="gallery-fullscreen-image"
            />
          </div>
          
          <button 
            onClick={() => navigateGallery('next')}
            className="absolute right-4 text-white p-2 hover:bg-white/10 rounded-full transition-colors"
            data-testid="gallery-next-btn"
          >
            <ChevronRight className="w-10 h-10" />
          </button>
          
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
            {images.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setSelectedImage(idx)}
                className={`w-3 h-3 rounded-full transition-colors ${
                  safeSelectedImage === idx ? 'bg-white' : 'bg-white/40 hover:bg-white/60'
                }`}
              />
            ))}
          </div>
          
          <p className="absolute bottom-12 left-1/2 -translate-x-1/2 text-white/80 text-sm">
            Use arrow keys to navigate • Press ESC to close
          </p>
        </div>
      )}

      {/* Property Info */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Title & Price */}
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold mb-2" data-testid="property-title">
                {property.title}
              </h1>
              <div className="flex items-center text-gray-600 mb-4">
                <MapPin className="w-4 h-4 mr-1" />
                <span>{property.address}, {property.locality}, {property.city}</span>
              </div>
              <div className="flex items-baseline gap-4">
                <span className="text-3xl sm:text-4xl font-bold text-primary" data-testid="property-price">
                  {formatPrice(property.price)}
                </span>
                {property.price_per_sqft && (
                  <span className="text-gray-500">
                    ₹{property.price_per_sqft.toLocaleString('en-IN')}/sqft
                  </span>
                )}
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 p-6 bg-[#F5F5F0] rounded-xl" data-testid="property-stats">
              {property.bedrooms && (
                <div className="text-center">
                  <Bed className="w-6 h-6 mx-auto mb-2 text-primary" />
                  <p className="font-semibold">{property.bedrooms} Beds</p>
                </div>
              )}
              {property.bathrooms && (
                <div className="text-center">
                  <Bath className="w-6 h-6 mx-auto mb-2 text-primary" />
                  <p className="font-semibold">{property.bathrooms} Baths</p>
                </div>
              )}
              <div className="text-center">
                <Maximize className="w-6 h-6 mx-auto mb-2 text-primary" />
                <p className="font-semibold" data-testid="property-area-badge">
                  <AreaBadge 
                    sqftValue={property.area_sqft} 
                    unit={property.area_input_unit || 'sqft'} 
                  />
                </p>
              </div>
              {property.parking && (
                <div className="text-center">
                  <Car className="w-6 h-6 mx-auto mb-2 text-primary" />
                  <p className="font-semibold">{property.parking} Parking</p>
                </div>
              )}
            </div>

            {/* Description */}
            <div>
              <h2 className="text-xl font-bold mb-4">About this property</h2>
              <p className="text-gray-700 leading-relaxed" data-testid="property-description">
                {property.description}
              </p>
            </div>

            {/* Property Details */}
            <div>
              <h2 className="text-xl font-bold mb-4">Property Details</h2>
              
              {/* Area with Full Breakdown */}
              {property.area_sqft && (
                <div className="p-5 bg-gradient-to-r from-primary/5 to-amber-50 rounded-xl mb-6" data-testid="property-area-section">
                  <h3 className="text-sm font-medium text-gray-500 mb-3 flex items-center gap-2">
                    <Ruler className="w-4 h-4" /> Property Area
                  </h3>
                  <AreaDisplay
                    sqftValue={property.area_sqft}
                    primaryUnit={property.area_input_unit || 'sqft'}
                    primaryValue={property.area_input_value}
                    showBreakdown={true}
                    maxBreakdownItems={4}
                    size="lg"
                  />
                </div>
              )}
              
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4" data-testid="property-details-grid">
                {property.carpet_area && (
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-500">Carpet Area</p>
                    <p className="font-semibold">{property.carpet_area.toLocaleString('en-IN')} sqft</p>
                  </div>
                )}
                {property.floor && (
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-500">Floor</p>
                    <p className="font-semibold">{property.floor} of {property.total_floors}</p>
                  </div>
                )}
                {property.age && (
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-500">Property Age</p>
                    <p className="font-semibold">{property.age}</p>
                  </div>
                )}
                {property.facing && (
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-500">Facing</p>
                    <p className="font-semibold">{property.facing}</p>
                  </div>
                )}
                {property.balconies && (
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-500">Balconies</p>
                    <p className="font-semibold">{property.balconies}</p>
                  </div>
                )}
                {property.workstations && (
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-500">Workstations</p>
                    <p className="font-semibold">{property.workstations}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Highlights */}
            {highlights.length > 0 && (
              <div>
                <h2 className="text-xl font-bold mb-4">Highlights</h2>
                <div className="flex flex-wrap gap-3" data-testid="property-highlights">
                  {highlights.map((highlight, idx) => (
                    <span key={idx} className="flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-medium">
                      <CheckCircle className="w-4 h-4" /> {highlight}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Amenities */}
            {amenities.length > 0 && (
              <div>
                <h2 className="text-xl font-bold mb-4">Amenities</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4" data-testid="property-amenities">
                  {amenities.map((amenity, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                      <CheckCircle className="w-4 h-4 text-primary" />
                      <span className="text-sm">{amenity}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Contact Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 bg-[#F5F5F0] p-6 rounded-xl space-y-6" data-testid="contact-sidebar">
              <div>
                <h3 className="font-bold text-lg mb-2">Listed by</h3>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
                    <Users className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold" data-testid="property-seller-name">{seller.name}</p>
                    <p className="text-sm text-gray-500">{seller.type}</p>
                    {property.agent_id && (
                      <Link
                        to={agentPath({ agent_id: property.agent_id, name: seller.name, city: property.city })}
                        className="text-xs font-medium text-primary hover:underline"
                        data-testid="view-agent-profile-link"
                      >
                        View agent profile →
                      </Link>
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <Button 
                  onClick={() => setContactModalOpen(true)}
                  className="w-full rounded-lg bg-primary py-6"
                  data-testid="contact-seller-btn"
                >
                  <Phone className="w-4 h-4 mr-2" /> Contact Seller
                </Button>
                <Button 
                  variant="outline"
                  className="w-full rounded-lg py-6"
                  data-testid="schedule-visit-btn"
                  onClick={() => setScheduleModalOpen(true)}
                >
                  <Calendar className="w-4 h-4 mr-2" /> Schedule Visit
                </Button>
              </div>

              <div className="pt-4 border-t">
                <p className="text-sm text-gray-500 mb-2">Contact directly:</p>
                {seller.phone && (
                  <a href={`tel:${seller.phone}`} className="flex items-center gap-2 text-sm hover:text-primary" data-testid="seller-phone-link">
                    <Phone className="w-4 h-4" /> {seller.phone}
                  </a>
                )}
                {seller.email && (
                  <a href={`mailto:${seller.email}`} className="flex items-center gap-2 text-sm hover:text-primary mt-2" data-testid="seller-email-link">
                    <Mail className="w-4 h-4" /> {seller.email}
                  </a>
                )}
                {!seller.phone && !seller.email && (
                  <p className="text-sm text-gray-400 italic">Use the form above to get in touch.</p>
                )}
              </div>

              {/* Interior Design CTA */}
              <div className="pt-4 border-t">
                <p className="text-sm text-gray-600 mb-3">Planning to renovate?</p>
                <Button 
                  variant="outline"
                  className="w-full rounded-lg"
                  onClick={() => navigate('/interior-design')}
                  data-testid="interior-design-cta"
                >
                  Get Interior Design Quote
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Seller Modal */}
      <Dialog open={contactModalOpen} onOpenChange={setContactModalOpen}>
        <DialogContent className="sm:max-w-[500px]" data-testid="contact-seller-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Phone className="w-5 h-5 text-primary" />
              Contact Seller
            </DialogTitle>
            <DialogDescription>
              Send your inquiry to the property owner. They will contact you shortly.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleContactSubmit} className="space-y-4 mt-4">
            <div>
              <Label>Your Name *</Label>
              <Input
                value={contactForm.name}
                onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                placeholder="Enter your name"
                required
                className="mt-1"
                data-testid="contact-name-input"
              />
            </div>
            <div>
              <Label>Phone Number *</Label>
              <Input
                type="tel"
                value={contactForm.phone}
                onChange={(e) => setContactForm({...contactForm, phone: e.target.value})}
                placeholder="+91 98765 43210"
                required
                className="mt-1"
                data-testid="contact-phone-input"
              />
            </div>
            <div>
              <Label>Email (Optional)</Label>
              <Input
                type="email"
                value={contactForm.email}
                onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                placeholder="you@email.com"
                className="mt-1"
                data-testid="contact-email-input"
              />
            </div>
            <div>
              <Label>Message (Optional)</Label>
              <Textarea
                value={contactForm.message}
                onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                placeholder="I'm interested in this property..."
                rows={3}
                className="mt-1"
                data-testid="contact-message-input"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setContactModalOpen(false)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" className="flex-1 bg-primary" data-testid="contact-submit-btn">
                Send Inquiry
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Schedule Visit Modal */}
      <Dialog open={scheduleModalOpen} onOpenChange={setScheduleModalOpen}>
        <DialogContent className="sm:max-w-[500px]" data-testid="schedule-visit-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-primary" />
              Schedule a Visit
            </DialogTitle>
            <DialogDescription>
              Book a time to visit this property. We'll confirm your appointment.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleScheduleSubmit} className="space-y-4 mt-4">
            <div>
              <Label>Your Name *</Label>
              <Input
                value={scheduleForm.name}
                onChange={(e) => setScheduleForm({...scheduleForm, name: e.target.value})}
                placeholder="Enter your name"
                required
                className="mt-1"
                data-testid="schedule-name-input"
              />
            </div>
            <div>
              <Label>Phone Number *</Label>
              <Input
                type="tel"
                value={scheduleForm.phone}
                onChange={(e) => setScheduleForm({...scheduleForm, phone: e.target.value})}
                placeholder="+91 98765 43210"
                required
                className="mt-1"
                data-testid="schedule-phone-input"
              />
            </div>
            <div>
              <Label>Email (Optional)</Label>
              <Input
                type="email"
                value={scheduleForm.email}
                onChange={(e) => setScheduleForm({...scheduleForm, email: e.target.value})}
                placeholder="you@email.com"
                className="mt-1"
                data-testid="schedule-email-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Preferred Date *</Label>
                <Input
                  type="date"
                  value={scheduleForm.preferred_date}
                  onChange={(e) => setScheduleForm({...scheduleForm, preferred_date: e.target.value})}
                  required
                  className="mt-1"
                  min={new Date().toISOString().split('T')[0]}
                  data-testid="schedule-date-input"
                />
              </div>
              <div>
                <Label>Preferred Time</Label>
                <Input
                  type="time"
                  value={scheduleForm.preferred_time}
                  onChange={(e) => setScheduleForm({...scheduleForm, preferred_time: e.target.value})}
                  className="mt-1"
                  data-testid="schedule-time-input"
                />
              </div>
            </div>
            <div>
              <Label>Additional Notes (Optional)</Label>
              <Textarea
                value={scheduleForm.notes}
                onChange={(e) => setScheduleForm({...scheduleForm, notes: e.target.value})}
                placeholder="Any specific requirements or questions..."
                rows={2}
                className="mt-1"
                data-testid="schedule-notes-input"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setScheduleModalOpen(false)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" className="flex-1 bg-primary" data-testid="schedule-submit-btn">
                Schedule Visit
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PropertyDetails;
